/**
 * AIAutopilotPanel — one-click AI Auto-Fill + Execute for a pipeline stage.
 *
 * Fetches data-driven suggestions from a backend `/auto-fill` endpoint,
 * shows them in a compact preview card with reason chips, and — on user
 * confirmation — POSTs the derived payload to the execute endpoint that
 * completes the stage.
 *
 * Consumers pass:
 *   - projectId        the project scope for both requests
 *   - title            headline text ("AI Auto-Fill · Ore shell", ...)
 *   - description      one-line context
 *   - autoFillPath     GET path relative to `/projects/{id}` e.g. "auto-fill/ore-shell"
 *   - buildPayload     fn(fields) → payload sent to execute
 *   - executePath      POST path (may be absolute or "/projects/{id}/..."). If
 *                      it starts with `/` it is used verbatim.
 *   - onSuccess        callback(response.data)
 *   - testid           prefix for data-testids
 */
import { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, CheckCircle2, AlertCircle, Play, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AIAutopilotPanel({
  projectId,
  title,
  description,
  autoFillPath,
  buildPayload,
  executePath,
  executeMethod = "POST",
  successLabel = "AI Auto-Fill complete",
  buttonLabel = "Run with AI values",
  onSuccess,
  testid = "ai-autopilot",
}) {
  const [state, setState] = useState({ loading: true, fields: null, eligible: false, reason: null, error: null });
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const fetchAutoFill = useCallback(async () => {
    if (!projectId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const url = `/projects/${projectId}/${autoFillPath}`;
      const { data } = await api.get(url);
      setState({
        loading: false,
        fields: data.fields || {},
        eligible: data.eligible !== false && Object.keys(data.fields || {}).length > 0,
        reason: data.reason || null,
        error: null,
      });
    } catch (e) {
      setState({
        loading: false, fields: null, eligible: false, reason: null,
        error: e?.response?.data?.detail || "Auto-fill fetch failed",
      });
    }
  }, [projectId, autoFillPath]);

  useEffect(() => { fetchAutoFill(); }, [fetchAutoFill]);

  const run = async () => {
    if (!state.fields) return;
    setRunning(true);
    try {
      // Flatten fields to {key: value} so consumers no longer need `.value`.
      // Also expose the full field objects on `payload._full` in case a
      // caller wants to introspect reason / source.
      const flat = Object.fromEntries(
        Object.entries(state.fields || {}).map(([k, v]) => [k, v?.value]),
      );
      const payload = buildPayload(flat, state.fields);
      const url = executePath.startsWith("/") ? executePath : `/projects/${projectId}/${executePath}`;
      const req = executeMethod === "POST" ? api.post(url, payload) : api.put(url, payload);
      const { data } = await req;
      setDone(true);
      toast.success(successLabel);
      onSuccess?.(data);
    } catch (e) {
      toast.error(`AI run failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const values = state.fields || {};
  const entries = Object.entries(values);

  return (
    <div
      className="border border-[#4CAF50]/40 bg-gradient-to-br from-lime-600/5 via-lime-600/[0.02] to-transparent rounded-md p-4"
      data-testid={`${testid}-panel`}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded bg-[#4CAF50]/15 border border-[#4CAF50]/40 shrink-0">
          <Sparkles className={`h-4 w-4 text-[#4CAF50] ${state.loading ? "animate-pulse" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-medium text-[#2E7D32]">{title}</div>
            <span className="text-[9.5px] uppercase tracking-[0.15em] text-[#2E7D32] font-mono">AI</span>
          </div>
          {description && (
            <div className="text-[11.5px] text-neutral-500 mt-0.5 leading-relaxed">{description}</div>
          )}

          {state.loading && (
            <div className="mt-3 flex items-center gap-2 text-[11.5px] text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyzing uploaded data…
            </div>
          )}

          {!state.loading && state.error && (
            <div className="mt-3 flex items-start gap-2 text-[11.5px] text-red-700"
                 data-testid={`${testid}-error`}>
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{state.error}</span>
            </div>
          )}

          {!state.loading && !state.error && !state.eligible && (
            <div className="mt-3 flex items-start gap-2 text-[11.5px] text-amber-700"
                 data-testid={`${testid}-not-eligible`}>
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{state.reason || "Not enough data uploaded yet to derive parameters. Import drillholes first."}</span>
            </div>
          )}

          {!state.loading && !state.error && state.eligible && (
            <>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`${testid}-values`}>
                {entries.map(([k, v]) => (
                  <div key={k} className="border border-neutral-200 rounded px-2.5 py-2 bg-white/50"
                       data-testid={`${testid}-field-${k}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-[0.1em] text-neutral-500 truncate">{k.replace(/_/g, " ")}</div>
                      <div className="text-[11px] font-mono tabular-nums text-[#2E7D32] truncate max-w-[60%]">{formatValue(v.value)}</div>
                    </div>
                    <div className="text-[10.5px] text-neutral-500 mt-0.5 leading-snug">{v.reason}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={run}
                  disabled={running || done}
                  className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4CAF50]/15 hover:bg-[#4CAF50]/25 disabled:opacity-60 border border-[#4CAF50]/50 rounded text-[12px] text-[#2E7D32] transition"
                  data-testid={`${testid}-run-btn`}
                >
                  {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    : <Play className="h-3.5 w-3.5" />}
                  {done ? "Done" : running ? "Running…" : buttonLabel}
                </button>
                <button
                  onClick={fetchAutoFill}
                  disabled={state.loading}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 border border-neutral-200 hover:border-neutral-300 rounded text-[11px] text-neutral-500 transition"
                  data-testid={`${testid}-refresh-btn`}
                  title="Re-analyze source data"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatValue(v) {
  if (v == null) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return `${v.length} items`;
  if (typeof v === "object") {
    // pull the most useful key
    const keys = Object.keys(v);
    if (keys.length === 0) return "—";
    const short = keys.slice(0, 3).map((k) => {
      const val = v[k];
      const disp = typeof val === "number" ? (Number.isInteger(val) ? val : val.toFixed(2)) : String(val);
      return `${k}=${disp}`;
    }).join(" · ");
    return short;
  }
  return String(v);
}
